/**
 * Canonical trigger types for the automation engine.
 * Maps trigger_type → display label, category, and default severity.
 */

export interface TriggerTypeInfo {
  value: string;
  label: string;
  category: 'system_health' | 'battery' | 'forecast' | 'solar' | 'usage' | 'scheduled';
  severity: 'critical' | 'warning' | 'info';
}

export const TRIGGER_TYPES: TriggerTypeInfo[] = [
  // System Health
  { value: 'system_offline', label: 'System offline', category: 'system_health', severity: 'critical' },
  { value: 'system_online', label: 'System online (recovered)', category: 'system_health', severity: 'info' },
  { value: 'capacity_changed', label: 'Capacity changed', category: 'system_health', severity: 'warning' },
  { value: 'provider_credential_invalid', label: 'Provider login rejected', category: 'system_health', severity: 'warning' },
  { value: 'provider_credential_mismatch', label: 'Provider account mismatch', category: 'system_health', severity: 'warning' },
  { value: 'provider_device_mismatch', label: 'Device inventory mismatch', category: 'system_health', severity: 'warning' },

  // Battery
  { value: 'reserve_breach', label: 'Reserve breach', category: 'battery', severity: 'critical' },
  { value: 'reserve_breach_imminent', label: 'Reserve breach imminent', category: 'battery', severity: 'critical' },
  { value: 'battery_critically_low', label: 'Battery critically low', category: 'battery', severity: 'critical' },
  { value: 'battery_health_degraded', label: 'Battery health degraded', category: 'battery', severity: 'warning' },
  { value: 'battery_warranty_approaching', label: 'Warranty window approaching', category: 'battery', severity: 'warning' },

  // Forecast
  { value: 'overnight_risk', label: 'Overnight risk', category: 'forecast', severity: 'warning' },
  { value: 'forecast_confidence_low', label: 'Forecast confidence low', category: 'forecast', severity: 'warning' },
  { value: 'data_stale_low_energy', label: 'Data stale (low energy)', category: 'system_health', severity: 'critical' },

  // Solar
  { value: 'curtailment_detected', label: 'Curtailment detected', category: 'solar', severity: 'info' },
  { value: 'curtailment_chronic', label: 'Chronic curtailment pattern', category: 'solar', severity: 'warning' },
  { value: 'pv_curtailment', label: 'Solar limited', category: 'solar', severity: 'info' },
  { value: 'excess_solar', label: 'Excess solar available', category: 'solar', severity: 'info' },

  // Usage
  { value: 'unusual_discharge', label: 'Unusual discharge', category: 'usage', severity: 'warning' },

  // Scheduled
  { value: 'scheduled_daily', label: 'Daily schedule', category: 'scheduled', severity: 'info' },
  { value: 'scheduled_weekly', label: 'Weekly schedule', category: 'scheduled', severity: 'info' },
  { value: 'scheduled_monthly', label: 'Monthly schedule', category: 'scheduled', severity: 'info' },
];

/** Trigger types the evaluator currently emits (evaluate-automations). Builder must limit to these. */
export const BUILDABLE_TRIGGER_TYPE_VALUES = [
  'reserve_breach_imminent',
  'system_offline',
  'data_stale_low_energy',
  'battery_critically_low',
  'overnight_risk',
  'resilience_risk_critical',
  'resilience_risk',
  'forecast_confidence_low',
  'pv_curtailment',
] as const;

export const BUILDABLE_TRIGGER_TYPES: TriggerTypeInfo[] = TRIGGER_TYPES.filter((t) =>
  (BUILDABLE_TRIGGER_TYPE_VALUES as readonly string[]).includes(t.value)
);

export const ACTION_TYPES = [
  { value: 'notification', label: 'Send notification', description: 'Alert via in-app, email, SMS, or push' },
  { value: 'system_control', label: 'System control', description: 'Write settings to inverter (requires approval)' },
  { value: 'report', label: 'Generate report', description: 'Create and deliver a report' },
  { value: 'proposal', label: 'Generate proposal', description: 'Create installer-reviewable proposal' },
  { value: 'webhook', label: 'Webhook', description: 'POST to external URL' },
  { value: 'service_ticket', label: 'Service ticket', description: 'Create internal maintenance ticket' },
] as const;

/** Action types allowed in the visual builder (DB CHECK; system_control excluded). */
export const BUILDER_ACTION_TYPES = ACTION_TYPES.filter((a) => a.value !== 'system_control');

export const CATEGORY_LABELS: Record<string, string> = {
  monitoring: 'Monitoring',
  battery_forecast: 'Battery & Forecast',
  solar_optimization: 'Solar & Optimization',
  reporting: 'Reporting',
  system_control: 'System Control',
};

export function getTriggerLabel(triggerType: string): string {
  return TRIGGER_TYPES.find((t) => t.value === triggerType)?.label ?? triggerType;
}

export function getActionLabel(actionType: string): string {
  return ACTION_TYPES.find((a) => a.value === actionType)?.label ?? actionType;
}

/** Cooldown presets for builder: value in minutes, label for display. */
export const COOLDOWN_PRESETS = [
  { value: 15, label: '15m' },
  { value: 60, label: '1h' },
  { value: 240, label: '4h' },
  { value: 1440, label: '24h' },
  { value: 10080, label: '7d' },
] as const;
